import { useState, useEffect, useRef } from "react";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

function fmt(s) {
  const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), sec = s%60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
}

const DUREES = [
  {lbl:"30 min",min:30},{lbl:"1h",min:60},{lbl:"2h",min:120},
  {lbl:"3h",min:180},{lbl:"½ journée",min:240},
  {lbl:"1 journée",min:480},{lbl:"2 jours",min:960},
];

const DONE_STATUTS = ["terminee","completed","cancelled","annulee"];

export default function Chrono({ T, intervention, onStarted, onDureeChange, onCloture, onPVIntermediaire }) {
  const isAlreadyDone = DONE_STATUTS.includes(intervention.statut);
  
  // Si terminée, calculer le temps figé une seule fois
  const frozenSeconds = intervention.started_at
    ? Math.floor((Date.now() - new Date(intervention.started_at).getTime()) / 1000)
    : 0;

  const [seconds, setSeconds]       = useState(frozenSeconds);
  const [running, setRunning]       = useState(!isAlreadyDone && !!intervention.started_at);
  const [duree, setDuree]           = useState(intervention.duree_estimee_min||120);
  // Durée fixée par l'admin — pas modifiable par le technicien
  const [showConfig, setShowConfig] = useState(!intervention.started_at);
  const [customMin, setCustomMin]   = useState("");
  const interval = useRef(null);

  useEffect(()=>{
    // Si déjà terminée → ne pas démarrer du tout
    if (isAlreadyDone) { setRunning(false); return; }
    // Si en cours → démarrer
    if (intervention.started_at) { setRunning(true); }
  },[]);

  useEffect(()=>{
    clearInterval(interval.current);
    if (running && !isAlreadyDone) {
      interval.current = setInterval(()=>setSeconds(s=>s+1), 1000);
    }
    return ()=>clearInterval(interval.current);
  },[running]);

  // GPS toutes les 30s
  useEffect(()=>{
    if (!running) return;
    const share = () => navigator.geolocation?.getCurrentPosition(pos=>{
      fetch(`${BASE}/interventions/${intervention.id}/position`,{
        method:"POST",headers:h(),
        body:JSON.stringify({lat:pos.coords.latitude,lng:pos.coords.longitude})
      }).catch(()=>{});
    });
    share();
    const t = setInterval(share,30000);
    return()=>clearInterval(t);
  },[running]);

  const start = async () => {
    await fetch(`${BASE}/interventions/${intervention.id}/start`,{method:"POST",headers:h()});
    setRunning(true); setShowConfig(false);
    onStarted&&onStarted(); onDureeChange&&onDureeChange(duree);
  };

  const minEcoules = Math.floor(seconds/60);
  const pct        = Math.min(100,(minEcoules/duree)*100);
  const enRetard   = running && minEcoules > duree;
  const barColor   = pct<60?T.success:pct<85?T.warning:T.danger;
  const fmtDuree   = (min) => min>=480?`${min/480}j`:min>=60?`${Math.floor(min/60)}h${min%60>0?min%60+"min":""}`:min+"min";

  return (
    <div style={{background:T.card,border:`2px solid ${isAlreadyDone?T.success:enRetard?T.danger:running?T.accent:T.border}`,borderRadius:16,padding:20,marginBottom:14}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:10,background:isAlreadyDone?`${T.success}22`:running?`${T.accent}22`:T.bg,border:`2px solid ${isAlreadyDone?T.success:running?T.accent:T.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
            {isAlreadyDone?"✅":running?"⏱️":"🕐"}
          </div>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:T.text}}>Chronomètre</div>
            <div style={{fontSize:11,color:isAlreadyDone?T.success:running?T.accent:T.muted}}>
              {isAlreadyDone?"✅ Intervention clôturée":running?"🔴 En cours · GPS partagé":"Configurez la durée estimée"}
            </div>
          </div>
        </div>
        {!running&&!isAlreadyDone&&<button onClick={()=>setShowConfig(!showConfig)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:13}}>⚙️</button>}
      </div>

      {/* Config */}
      {showConfig&&!running&&!isAlreadyDone&&(
        <div style={{marginBottom:14,background:T.bg,borderRadius:11,padding:14,border:`1px solid ${T.border}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:10}}>Durée estimée</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
            {DUREES.map(d=>(
              <button key={d.min} onClick={()=>setDuree(d.min)}
                style={{padding:"7px 11px",borderRadius:9,border:`1.5px solid ${duree===d.min?T.accent:T.border}`,background:duree===d.min?T.accentDim:"transparent",color:duree===d.min?T.accent:T.muted,fontSize:11,fontWeight:duree===d.min?700:500,cursor:"pointer"}}>
                {d.lbl}
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8}}>
            <input type="number" value={customMin} onChange={e=>setCustomMin(e.target.value)} placeholder="Minutes personnalisées..."
              style={{flex:1,background:T.card,border:`1px solid ${T.border}`,borderRadius:9,padding:"8px 12px",color:T.text,fontSize:13,outline:"none"}}/>
            <button onClick={()=>customMin&&setDuree(parseInt(customMin))} disabled={!customMin}
              style={{padding:"8px 13px",borderRadius:9,background:customMin?T.accentDim:T.bg,border:`1px solid ${customMin?T.accent:T.border}`,color:customMin?T.accent:T.muted,fontSize:12,cursor:"pointer"}}>OK</button>
          </div>
          <div style={{marginTop:8,fontSize:12,color:T.muted}}>Durée : <strong style={{color:T.text}}>{fmtDuree(duree)}</strong></div>
        </div>
      )}

      {/* Temps */}
      <div style={{textAlign:"center",marginBottom:14}}>
        <div style={{fontSize:52,fontWeight:800,color:isAlreadyDone?T.success:enRetard?T.danger:T.text,fontFamily:"monospace",letterSpacing:"3px",lineHeight:1}}>
          {fmt(seconds)}
        </div>
        <div style={{marginTop:6,fontSize:12,fontWeight:600,color:isAlreadyDone?T.success:enRetard?T.danger:T.muted}}>
          {isAlreadyDone
            ? "Temps total enregistré"
            : !running&&seconds===0 ? "Démarrez pour lancer le compteur"
            : enRetard ? `⚠️ En retard de ${fmtDuree(minEcoules-duree)}`
            : running ? `Reste ~${fmtDuree(duree-minEcoules)} · ${pct.toFixed(0)}% écoulé`
            : `Temps : ${fmt(seconds)}`}
        </div>
      </div>

      {/* Barre */}
      {(running||isAlreadyDone)&&seconds>0&&(
        <div style={{marginBottom:14}}>
          <div style={{height:8,background:T.border,borderRadius:4,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${Math.min(100,pct)}%`,background:isAlreadyDone?T.success:`linear-gradient(90deg,${T.success},${barColor})`,borderRadius:4,transition:"width 1s"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.muted,marginTop:4}}>
            <span>0</span>
            <span style={{color:isAlreadyDone?T.success:barColor,fontWeight:700}}>{pct.toFixed(0)}%</span>
            <span>{fmtDuree(duree)}</span>
          </div>
        </div>
      )}

      {/* Boutons */}
      {isAlreadyDone ? (
        <div style={{padding:"12px 16px",background:`${T.success}15`,border:`1px solid ${T.success}50`,borderRadius:11,textAlign:"center",fontSize:13,fontWeight:700,color:T.success}}>
          ✅ Clôturée · Temps total : {fmt(seconds)}
        </div>
      ) : !running&&seconds===0 ? (
        <button onClick={start} style={{width:"100%",padding:"13px",borderRadius:12,background:`linear-gradient(135deg,${T.success},#16a34a)`,border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer"}}>
          ▶ Démarrer l'intervention
        </button>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <button onClick={onPVIntermediaire}
            style={{padding:"13px 10px",borderRadius:12,background:`${T.warning}18`,border:`2px solid ${T.warning}50`,color:T.warning,fontSize:13,fontWeight:800,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <span style={{fontSize:20}}>📋</span>
            <span>PV de journée</span>
            <span style={{fontSize:10,fontWeight:400,color:T.muted}}>Continuer demain</span>
          </button>
          <button onClick={onCloture}
            style={{padding:"13px 10px",borderRadius:12,background:`linear-gradient(135deg,${T.success},#16a34a)`,border:"none",color:"#fff",fontSize:13,fontWeight:800,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <span style={{fontSize:20}}>✅</span>
            <span>Clôturer</span>
            <span style={{fontSize:10,fontWeight:400,color:"rgba(255,255,255,0.8)"}}>Signer & fermer</span>
          </button>
        </div>
      )}
    </div>
  );
}
